import React, { useEffect, useMemo, useState } from "react";
import { Save, X, Plus, Trash2, Lock, RefreshCw } from "lucide-react";
import { useNavigate } from "react-router-dom";

// Mirrors TRINETRA_CUSTOM_KEY_PATTERN in be/config/trinetraConstants.js. Kept
// in sync by hand; the backend is the one that actually enforces it.
const CUSTOM_KEY_PATTERN = /^[a-z][a-z0-9_]{1,47}$/;
const CUSTOM_KEY_HINT = "lower_snake_case, 2-48 chars, starting with a letter";

// Matches the Joi rule in be/validations/trinetraLicense/trinetraLicenseValidation.js.
const DESCRIPTION_MAX = 200;

const STATUS_STYLE = {
  ready: { color: "var(--success)", label: "Ready" },
  in_progress: { color: "#f59e0b", label: "In progress" },
  planned: { color: "var(--main-text-muted)", label: "Planned" },
};

const RequiredLabel = ({ children }) => (
  <label className="text-sm font-medium mb-1 block" style={{ color: "var(--main-text-muted)" }}>
    {children} <span style={{ color: "var(--danger)" }}>*</span>
  </label>
);

const OptionalLabel = ({ children, hint }) => (
  <label className="text-sm font-medium mb-1 block" style={{ color: "var(--main-text-muted)" }}>
    {children}
    {hint && <span className="ml-1.5 text-xs font-normal opacity-70">{hint}</span>}
  </label>
);

const emptyParameter = () => ({ key: "", type: "scalar", value: "" });

const blankForm = () => ({
  clientId: "",
  description: "",
  hardwareFingerprint: "",
  licenseType: "TRIAL",
  startDate: "",
  endDate: "",
  maxCameras: "",
  maxAiModels: "",
  maxNodes: "",
  allowedModels: [],
  customModels: [],
  trinetraFeatures: [],
  customFeatures: [],
  customParameters: [],
});

const catalogKeysOf = (config) =>
  config.featureCatalog?.length
    ? config.featureCatalog.map((f) => f.key)
    : config.features || [];

/**
 * Form state for a license that already exists.
 *
 * The stored row splits models and features into standard and custom, but that
 * split records what was standard *when the license was issued* — and the
 * pickers here, like the validation behind them, work off today's catalog. A
 * key promoted into the catalog since has to appear as a standard option, and
 * one dropped from it has to fall back to a custom chip, or the edit is
 * rejected for sending a catalogued key as custom. So the split is recomputed
 * from the merged lists rather than read from the row.
 *
 * Custom parameter values come back as they were signed — an array for a list,
 * a scalar otherwise — and are flattened into the comma-separated text the
 * editor works in.
 */
const formFromLicense = (license, config) => {
  const standardModels = config.aiModels || [];
  const catalogKeys = catalogKeysOf(config);

  const models = license.allowedModels || [];
  const features = license.trinetraFeatures || [];

  return {
    ...blankForm(),
    clientId: license.clientObjectId?._id || license.clientObjectId || "",
    description: license.description || "",
    hardwareFingerprint: license.hardwareFingerprint || "",
    licenseType: license.licenseType || "TRIAL",
    maxCameras: String(license.maxCameras ?? ""),
    maxAiModels: String(license.maxAiModels ?? ""),
    maxNodes: String(license.maxNodes ?? ""),
    allowedModels: models.filter((m) => standardModels.includes(m)),
    customModels: models.filter((m) => !standardModels.includes(m)),
    trinetraFeatures: features.filter((f) => catalogKeys.includes(f)),
    customFeatures: features.filter((f) => !catalogKeys.includes(f)),
    customParameters: (license.customParameters || []).map(({ key, value }) => ({
      key,
      type: Array.isArray(value) ? "list" : "scalar",
      value: Array.isArray(value) ? value.join(", ") : String(value),
    })),
  };
};

/**
 * Issues a Trinetra license, or edits the entitlements of one that exists.
 *
 * The two modes share every control that decides what a license grants, and
 * differ only in what they are allowed to touch. An edit re-signs a license in
 * place: the client, the machine it is bound to, its type and its validity
 * window are fixed for the life of the license, so in edit mode they are shown
 * as a locked summary rather than as fields. What remains editable is exactly
 * the three groups below — resource limits, features & models, and custom
 * parameters.
 *
 * @param {"create"|"edit"} mode
 * @param {object} license The row being edited. Required when mode is "edit".
 */
export default function TrinetraLicenseGenerationForm({
  onSubmit,
  isLoading,
  clients = [],
  config = {},
  mode = "create",
  license = null,
}) {
  const navigate = useNavigate();

  const isEdit = mode === "edit" && Boolean(license);

  // Built once. Re-deriving it on every render would discard what the operator
  // has typed each time the parent re-renders.
  const [form, setForm] = useState(() =>
    isEdit ? formFromLicense(license, config) : blankForm()
  );

  // Text sitting in the "add a custom key" boxes, keyed by the field it feeds.
  const [drafts, setDrafts] = useState({ customModels: "", customFeatures: "" });
  const [errors, setErrors] = useState({});

  // The catalog carries labels and descriptions; `features` is the flat key
  // list older callers pass. Fall back to it so the form still renders when
  // only keys are available.
  const featureCatalog = useMemo(() => {
    if (config.featureCatalog?.length) return config.featureCatalog;
    return (config.features || []).map((key) => ({ key, label: key }));
  }, [config.featureCatalog, config.features]);

  const catalogKeys = useMemo(
    () => featureCatalog.map((f) => f.key),
    [featureCatalog]
  );

  const reservedParameterKeys = config.reservedParameterKeys || [];
  const standardModels = config.aiModels || [];

  const availableModels = standardModels.filter(
    (m) => !form.allowedModels.includes(m)
  );

  // maxAiModels caps standard and custom models together, so every add path
  // measures against the merged count. An unset limit means no cap yet — the
  // required-field check catches that at submit.
  const modelCount = form.allowedModels.length + form.customModels.length;
  const modelCap = Number(form.maxAiModels) > 0 ? Number(form.maxAiModels) : Infinity;
  const modelSlotsLeft = modelCap - modelCount;

  useEffect(() => {
    if (form.licenseType === "PERPETUAL") {
      setForm((prev) => ({ ...prev, endDate: "" }));
    }
  }, [form.licenseType]);

  const validate = () => {
    const e = {};

    // The client, the machine and the dates are only editable at issue; in edit
    // mode they are displayed, not collected, so there is nothing to check.
    if (!isEdit) {
      if (!form.clientId) e.clientId = "Select a client";
      if (!form.hardwareFingerprint.trim()) {
        e.hardwareFingerprint = "Hardware fingerprint is required";
      } else if (!/^[a-fA-F0-9]{64}$/.test(form.hardwareFingerprint.trim())) {
        e.hardwareFingerprint = "Must be exactly 64 hex characters";
      }

      if (!form.startDate) {
        e.startDate = "Start date required";
      } else {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const selectedStartDate = new Date(form.startDate + "T00:00:00");

        if (selectedStartDate < today) {
          e.startDate = "Cannot be in the past";
        }
      }

      if (form.licenseType === "TRIAL") {
        if (!form.endDate) e.endDate = "End date required";
        else if (new Date(form.endDate) <= new Date(form.startDate))
          e.endDate = "Must be after start date";
      }
    }

    if (form.description.trim().length > DESCRIPTION_MAX) {
      e.description = `Cannot exceed ${DESCRIPTION_MAX} characters`;
    }

    if (!form.maxCameras || Number(form.maxCameras) <= 0) e.maxCameras = "Must be greater than 0";
    if (!form.maxNodes || Number(form.maxNodes) <= 0) e.maxNodes = "Must be greater than 0";
    if (!form.maxAiModels || Number(form.maxAiModels) <= 0) e.maxAiModels = "Must be greater than 0";

    // A license may be built from custom entries alone, so both requirements
    // are on the total rather than on the standard selection.
    if (modelCount === 0) e.allowedModels = "Select at least one model";
    if (form.trinetraFeatures.length + form.customFeatures.length === 0) {
      e.trinetraFeatures = "Select at least one feature";
    }

    if (modelCount > Number(form.maxAiModels)) {
      e.allowedModels = "Allowed models exceed max AI models";
    }

    const paramErrors = {};
    const seenKeys = new Set();

    form.customParameters.forEach((param, index) => {
      const key = param.key.trim();
      const value = param.value.trim();

      if (!key && !value) {
        paramErrors[index] = "Key and value are required";
        return;
      }
      if (!CUSTOM_KEY_PATTERN.test(key)) {
        paramErrors[index] = `Key must be ${CUSTOM_KEY_HINT}`;
        return;
      }
      if (reservedParameterKeys.includes(key)) {
        paramErrors[index] = "Reserved key — set it with the field above instead";
        return;
      }
      if (seenKeys.has(key)) {
        paramErrors[index] = "Duplicate key";
        return;
      }
      if (!value) {
        paramErrors[index] = "Value is required";
        return;
      }
      if (param.type === "list") {
        const items = value.split(",").map((v) => v.trim()).filter(Boolean);
        if (items.length === 0) {
          paramErrors[index] = "Enter at least one comma-separated value";
          return;
        }
      }

      seenKeys.add(key);
    });

    if (Object.keys(paramErrors).length > 0) e.customParameters = paramErrors;

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (ev) => {
    ev.preventDefault();
    if (!validate()) return;

    // An edit sends the entitlements alone. The backend reads the client, the
    // fingerprint, the type and the dates off the stored record and rejects the
    // request outright if it carries them, so sending them would fail the edit.
    const payload = {
      ...(isEdit
        ? {}
        : {
          clientId: form.clientId,
          hardwareFingerprint: form.hardwareFingerprint.trim(),
          licenseType: form.licenseType,
          startDate: form.startDate,
        }),
      description: form.description.trim(),
      maxCameras: Number(form.maxCameras),
      maxAiModels: Number(form.maxAiModels),
      maxNodes: Number(form.maxNodes),
      allowedModels: form.allowedModels,
      customModels: form.customModels,
      trinetraFeatures: form.trinetraFeatures,
      customFeatures: form.customFeatures,
      customParameters: form.customParameters.map((param) => ({
        key: param.key.trim(),
        value:
          param.type === "list"
            ? param.value.split(",").map((v) => v.trim()).filter(Boolean)
            : param.value.trim(),
      })),
    };

    if (!isEdit && form.licenseType === "TRIAL") {
      payload.endDate = form.endDate;
    }

    onSubmit(payload);
  };

  const setError = (field, message) =>
    setErrors((prev) => ({ ...prev, [field]: message }));

  const addChip = (field, value) => {
    if (!value || form[field].includes(value)) return;
    if (field === "allowedModels" && modelSlotsLeft <= 0) {
      setError("allowedModels", "Max AI Models reached — raise the limit to add more");
      return;
    }

    setForm((prev) => ({
      ...prev,
      [field]: [...prev[field], value]
    }));
    setError(field, undefined);
  };

  const removeChip = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: prev[field].filter((v) => v !== value) }));
  };

  /**
   * Fills the standard model list, stopping at maxAiModels. Truncating without
   * saying so would read as "all models are licensed" when they are not, so a
   * partial fill reports what it left out.
   */
  const selectAllModels = () => {
    const toAdd = availableModels.slice(0, Math.max(0, modelSlotsLeft));

    setForm((prev) => ({ ...prev, allowedModels: [...prev.allowedModels, ...toAdd] }));
    setError(
      "allowedModels",
      toAdd.length < availableModels.length
        ? `Added ${toAdd.length} of ${availableModels.length} — raise Max AI Models for the rest`
        : undefined
    );
  };

  const toggleFeature = (key) => {
    setForm((prev) => ({
      ...prev,
      trinetraFeatures: prev.trinetraFeatures.includes(key)
        ? prev.trinetraFeatures.filter((f) => f !== key)
        : [...prev.trinetraFeatures, key],
    }));
    setError("trinetraFeatures", undefined);
  };

  const setAllFeatures = (selected) => {
    setForm((prev) => ({ ...prev, trinetraFeatures: selected ? [...catalogKeys] : [] }));
    setError("trinetraFeatures", undefined);
  };

  /**
   * Commits the draft in one of the custom-key boxes.
   *
   * `standardKeys` are the keys already offered by the picker above the box —
   * re-adding one as custom would produce a duplicate entitlement that the
   * backend rejects, so it is caught here with a pointer to the right control.
   */
  const addCustomKey = (field, standardKeys) => {
    const key = drafts[field].trim().toLowerCase();
    if (!key) return;

    const draftError = `${field}Draft`;

    if (standardKeys.includes(key)) {
      setError(draftError, "Already a standard option — select it from the list above");
      return;
    }
    if (form[field].includes(key)) {
      setError(draftError, "Already added");
      return;
    }
    if (!CUSTOM_KEY_PATTERN.test(key)) {
      setError(draftError, `Must be ${CUSTOM_KEY_HINT}`);
      return;
    }
    if (field === "customModels" && modelSlotsLeft <= 0) {
      setError(draftError, "Max AI Models reached — raise the limit to add more");
      return;
    }

    setForm((prev) => ({ ...prev, [field]: [...prev[field], key] }));
    setDrafts((prev) => ({ ...prev, [field]: "" }));
    setErrors((prev) => ({
      ...prev,
      [draftError]: undefined,
      // The entry just added may be the one that satisfies "at least one".
      [field === "customModels" ? "allowedModels" : "trinetraFeatures"]: undefined,
    }));
  };

  const updateParameter = (index, patch) => {
    setForm((prev) => ({
      ...prev,
      customParameters: prev.customParameters.map((param, i) =>
        i === index ? { ...param, ...patch } : param
      ),
    }));
  };

  const removeParameter = (index) => {
    setForm((prev) => ({
      ...prev,
      customParameters: prev.customParameters.filter((_, i) => i !== index),
    }));
  };

  const inputStyle = {
    background: "var(--input-background)",
    color: "var(--main-text)",
    borderColor: "var(--card-border)",
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5 max-h-[70vh] overflow-y-auto pr-2">
      <style>{`
        .no-spinner::-webkit-outer-spin-button,
        .no-spinner::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }

        .no-spinner[type=number] {
          -moz-appearance: textfield;
          appearance: textfield;
        }
      `}</style>

      {/* WHAT AN EDIT CANNOT CHANGE */}
      {isEdit && <LockedLicenseSummary license={license} clients={clients} />}

      {/* CLIENT SELECTION */}
      {!isEdit && (
        <div>
          <div className="flex items-center gap-3 mb-1">
            <RequiredLabel>Client</RequiredLabel>
            <label className="flex items-center gap-1.5 text-xs cursor-pointer select-none mb-1" style={{
              color: "var(--main-text-muted)"
            }}>
              <input
                type="checkbox"
                onChange={(e) => {
                  if (e.target.checked) {
                    navigate("/client");
                  }
                }}
                className="w-3.5 h-3.5"
              />
              Create New Client
            </label>
          </div>

          {clients.length === 0 ? (
            <div className="w-full h-10 rounded-lg border px-3 flex items-center" style={{ background: "var(--input-background)", borderColor: "var(--card-border)" }}>
              <span className="text-sm" style={{ color: "var(--main-text-muted)" }}>No clients found. Use "Create New Client" above.</span>
            </div>
          ) : (
            <select
              value={form.clientId}
              onChange={(e) => setForm({ ...form, clientId: e.target.value })}
              className="w-full h-10 px-3 rounded-lg border focus:ring-0 transition"
              style={inputStyle}
            >
              <option value="">Select Existing Client</option>
              {clients.map((c) => (
                <option key={c._id} value={c._id}>
                  {c.clientName} ({c.clientID || c.clientId})
                </option>
              ))}
            </select>
          )}
          {errors.clientId && <p className="text-xs mt-1" style={{ color: "var(--danger)" }}>{errors.clientId}</p>}
        </div>
      )}

      {/* DESCRIPTION */}
      <div>
        <OptionalLabel hint="(shown in the license list)">Description</OptionalLabel>
        <input
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          maxLength={DESCRIPTION_MAX}
          placeholder="e.g. Pune DC — 2nd floor cameras"
          className="w-full h-10 px-3 text-sm rounded-lg border focus:ring-0 transition"
          style={inputStyle}
        />
        <div className="flex justify-between mt-1 gap-3">
          {errors.description ? (
            <p className="text-xs" style={{ color: "var(--danger)" }}>{errors.description}</p>
          ) : (
            <span className="text-xs" style={{ color: "var(--main-text-muted)" }}>
              A short label to recognise this license by, instead of its generated ID
            </span>
          )}
          <span
            className="text-xs shrink-0"
            style={{ color: "var(--main-text-muted)" }}
          >
            {form.description.length}/{DESCRIPTION_MAX}
          </span>
        </div>
      </div>

      {!isEdit && (
        <>
          {/* HARDWARE FINGERPRINT */}
          <div>
            <RequiredLabel>Hardware Fingerprint</RequiredLabel>
            <textarea
              rows={1}
              value={form.hardwareFingerprint}
              onChange={(e) => setForm({ ...form, hardwareFingerprint: e.target.value.replace(/\s/g, "") })}
              placeholder="Enter 64 hex characters (e.g. a1b2c3d4e5f6...)"
              className="w-full px-3 py-2 font-mono text-sm rounded-lg border focus:ring-0 transition resize-none break-all"
              style={inputStyle}
            />
            <div className="flex justify-between mt-1">
              {errors.hardwareFingerprint ? (
                <p className="text-xs" style={{ color: "var(--danger)" }}>{errors.hardwareFingerprint}</p>
              ) : (
                <span className="text-xs" style={{ color: "var(--main-text-muted)" }}>SHA-256 hardware fingerprint</span>
              )}
              <span className="text-xs" style={{ color: form.hardwareFingerprint.length === 64 ? "var(--success)" : "var(--main-text-muted)" }}>
                {form.hardwareFingerprint.length}/64
              </span>
            </div>
          </div>

          {/* LICENSE TYPE */}
          <div>
            <RequiredLabel>License Type</RequiredLabel>
            <select
              value={form.licenseType}
              onChange={(e) => setForm({ ...form, licenseType: e.target.value })}
              className="w-full h-10 px-3 rounded-lg border focus:ring-0 transition"
              style={inputStyle}
            >
              <option value="TRIAL">Trial</option>
              <option value="PERPETUAL">Perpetual</option>
            </select>
          </div>

          {/* DATES */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <RequiredLabel>Valid From</RequiredLabel>
              <input
                type="date"
                value={form.startDate}
                onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                className="w-full h-10 px-3 rounded-lg border focus:ring-0 transition"
                style={inputStyle}
              />
              {errors.startDate ? (
                <p className="text-xs mt-1" style={{ color: "var(--danger)" }}>{errors.startDate}</p>
              ) : (
                <p className="text-xs mt-1" style={{ color: "var(--main-text-muted)" }}>
                  The license activates as soon as it is generated
                </p>
              )}
            </div>

            {form.licenseType === "TRIAL" && (
              <div>
                <RequiredLabel>Valid Until</RequiredLabel>
                <input
                  type="date"
                  value={form.endDate}
                  onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                  className="w-full h-10 px-3 rounded-lg border focus:ring-0 transition"
                  style={inputStyle}
                />
                {errors.endDate && <p className="text-xs mt-1" style={{ color: "var(--danger)" }}>{errors.endDate}</p>}
              </div>
            )}
          </div>
        </>
      )}

      {/* SECTION DIVIDER */}
      <div className="border-t pt-4" style={{ borderColor: "var(--card-border)" }}>
        <p className="text-sm font-semibold mb-3" style={{ color: "var(--main-text-muted)" }}>Resource Limits</p>
      </div>

      {/* LIMITS */}
      <div className="grid grid-cols-3 gap-4">
        <div>
          <RequiredLabel>Max Cameras</RequiredLabel>
          <input
            type="number"
            min="1"
            value={form.maxCameras}
            onChange={(e) => setForm({ ...form, maxCameras: e.target.value })}
            placeholder="e.g. 64"
            className="no-spinner w-full h-10 px-3 rounded-lg border focus:ring-0 transition"
            style={inputStyle}
          />
          {errors.maxCameras && <p className="text-xs mt-1" style={{ color: "var(--danger)" }}>{errors.maxCameras}</p>}
        </div>
        <div>
          <RequiredLabel>Max AI Models</RequiredLabel>
          <input
            type="number"
            min="1"
            value={form.maxAiModels}
            onChange={(e) => setForm({ ...form, maxAiModels: e.target.value })}
            placeholder="e.g. 5"
            className="no-spinner w-full h-10 px-3 rounded-lg border focus:ring-0 transition"
            style={inputStyle}
          />
          {errors.maxAiModels && <p className="text-xs mt-1" style={{ color: "var(--danger)" }}>{errors.maxAiModels}</p>}
        </div>
        <div>
          <RequiredLabel>Max Nodes</RequiredLabel>
          <input
            type="number"
            min="1"
            value={form.maxNodes}
            onChange={(e) => setForm({ ...form, maxNodes: e.target.value })}
            placeholder="e.g. 3"
            className="no-spinner w-full h-10 px-3 rounded-lg border focus:ring-0 transition"
            style={inputStyle}
          />
          {errors.maxNodes && <p className="text-xs mt-1" style={{ color: "var(--danger)" }}>{errors.maxNodes}</p>}
        </div>
      </div>

      {/* SECTION DIVIDER */}
      <div className="border-t pt-4" style={{ borderColor: "var(--card-border)" }}>
        <p className="text-sm font-semibold mb-3" style={{ color: "var(--main-text-muted)" }}>Features & Models</p>
      </div>

      {/* ALLOWED MODELS (chips) */}
      <ChipSelect
        label="Allowed AI Models"
        selected={form.allowedModels}
        available={availableModels}
        onAdd={(v) => addChip("allowedModels", v)}
        onRemove={(v) => removeChip("allowedModels", v)}
        onSelectAll={selectAllModels}
        onClearAll={() => setForm((prev) => ({ ...prev, allowedModels: [] }))}
        error={errors.allowedModels}
        placeholder="Select models to allow"
        countSuffix={
          form.customModels.length > 0
            ? ` + ${form.customModels.length} custom`
            : ""
        }
      />

      {/* TRINETRA FEATURES (catalog) */}
      <div>
        <div className="flex items-center justify-between">
          <RequiredLabel>Trinetra Features</RequiredLabel>
          <BulkActions
            onSelectAll={() => setAllFeatures(true)}
            onClearAll={() => setAllFeatures(false)}
            disabled={featureCatalog.length === 0}
          />
        </div>

        <div
          className="rounded-lg border divide-y max-h-72 overflow-y-auto"
          style={{ background: "var(--input-background)", borderColor: "var(--card-border)" }}
        >
          {featureCatalog.length === 0 && (
            <p className="px-3 py-4 text-sm italic" style={{ color: "var(--main-text-muted)" }}>
              No features available
            </p>
          )}

          {featureCatalog.map((feature) => {
            const checked = form.trinetraFeatures.includes(feature.key);
            const status = STATUS_STYLE[feature.status] || STATUS_STYLE.planned;

            return (
              <label
                key={feature.key}
                className="flex gap-3 px-3 py-2.5 cursor-pointer transition hover:opacity-90"
                style={{
                  borderColor: "var(--card-border)",
                  background: checked ? "var(--brand-light)" : "transparent",
                }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleFeature(feature.key)}
                  className="mt-0.5 w-4 h-4 shrink-0"
                />

                <span className="min-w-0">
                  <span className="flex items-center flex-wrap gap-2">
                    <span className="text-sm font-medium" style={{ color: "var(--main-text)" }}>
                      {feature.label || feature.key}
                    </span>
                    <code
                      className="text-[11px] font-mono px-1.5 py-0.5 rounded"
                      style={{ background: "var(--card-border)", color: "var(--main-text-muted)" }}
                    >
                      {feature.key}
                    </code>
                    <span
                      className="inline-flex items-center gap-1 text-[11px]"
                      style={{ color: status.color }}
                      title={`Status: ${status.label}`}
                    >
                      <span
                        className="w-2 h-2 rounded-full inline-block"
                        style={{ background: status.color }}
                      />
                      {status.label}
                    </span>
                  </span>

                  {feature.covers && (
                    <span className="block text-xs mt-0.5" style={{ color: "var(--main-text-muted)" }}>
                      {feature.covers}
                    </span>
                  )}
                  {feature.owningService && (
                    <span className="block text-[11px] mt-0.5 opacity-75" style={{ color: "var(--main-text-muted)" }}>
                      Owner: {feature.owningService}
                    </span>
                  )}
                </span>
              </label>
            );
          })}
        </div>

        <div className="flex justify-between mt-1">
          {errors.trinetraFeatures ? (
            <p className="text-xs" style={{ color: "var(--danger)" }}>{errors.trinetraFeatures}</p>
          ) : (
            <span className="text-xs" style={{ color: "var(--main-text-muted)" }}>
              {form.trinetraFeatures.length} of {featureCatalog.length} selected
            </span>
          )}
        </div>
      </div>

      {/* SECTION DIVIDER */}
      <div className="border-t pt-4" style={{ borderColor: "var(--card-border)" }}>
        <p className="text-sm font-semibold" style={{ color: "var(--main-text-muted)" }}>Custom Entitlements</p>
        <p className="text-xs mt-1" style={{ color: "var(--main-text-muted)" }}>
          Signed into the license alongside the standard ones. Use these for a model,
          flag or limit the lists above do not cover yet — the validator hands the
          keys back to the application exactly as entered.
        </p>
      </div>

      {/* CUSTOM AI MODELS */}
      <CustomKeyAdder
        label="Custom AI Models"
        placeholder="e.g. ppe_detection"
        draft={drafts.customModels}
        onDraftChange={(v) => {
          setDrafts((prev) => ({ ...prev, customModels: v }));
          setError("customModelsDraft", undefined);
        }}
        onAdd={() => addCustomKey("customModels", standardModels)}
        items={form.customModels}
        onRemove={(key) => removeChip("customModels", key)}
        error={errors.customModelsDraft}
        note={
          modelCap === Infinity
            ? "Counts against Max AI Models, like a standard model"
            : `${modelCount} of ${modelCap} model slots used`
        }
        inputStyle={inputStyle}
      />

      {/* CUSTOM FEATURES */}
      <CustomKeyAdder
        label="Custom Features"
        placeholder="e.g. visitor_management"
        draft={drafts.customFeatures}
        onDraftChange={(v) => {
          setDrafts((prev) => ({ ...prev, customFeatures: v }));
          setError("customFeaturesDraft", undefined);
        }}
        onAdd={() => addCustomKey("customFeatures", catalogKeys)}
        items={form.customFeatures}
        onRemove={(key) => removeChip("customFeatures", key)}
        error={errors.customFeaturesDraft}
        inputStyle={inputStyle}
      />

      {/* CUSTOM PARAMETERS */}
      <div>
        <OptionalLabel hint="(extra signed limits)">Custom Parameters</OptionalLabel>

        {form.customParameters.length === 0 && (
          <p className="text-xs mb-2 italic" style={{ color: "var(--main-text-muted)" }}>
            None added
          </p>
        )}

        <div className="space-y-2">
          {form.customParameters.map((param, index) => (
            <div key={index}>
              <div className="flex gap-2">
                <input
                  value={param.key}
                  onChange={(e) => updateParameter(index, { key: e.target.value })}
                  placeholder="key"
                  className="w-1/3 h-10 px-3 font-mono text-sm rounded-lg border focus:ring-0 transition"
                  style={inputStyle}
                />

                <select
                  value={param.type}
                  onChange={(e) => updateParameter(index, { type: e.target.value })}
                  className="h-10 px-2 text-sm rounded-lg border focus:ring-0 transition"
                  style={inputStyle}
                >
                  <option value="scalar">Value</option>
                  <option value="list">List</option>
                </select>

                <input
                  value={param.value}
                  onChange={(e) => updateParameter(index, { value: e.target.value })}
                  placeholder={param.type === "list" ? "value1, value2, value3" : "e.g. 25"}
                  className="flex-1 h-10 px-3 font-mono text-sm rounded-lg border focus:ring-0 transition"
                  style={inputStyle}
                />

                <button
                  type="button"
                  onClick={() => removeParameter(index)}
                  title="Remove parameter"
                  className="h-10 px-3 rounded-lg border inline-flex items-center transition hover:opacity-80"
                  style={{ color: "var(--danger)", borderColor: "var(--danger)" }}
                >
                  <Trash2 size={15} />
                </button>
              </div>

              {errors.customParameters?.[index] && (
                <p className="text-xs mt-1" style={{ color: "var(--danger)" }}>
                  {errors.customParameters[index]}
                </p>
              )}
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={() =>
            setForm((prev) => ({
              ...prev,
              customParameters: [...prev.customParameters, emptyParameter()],
            }))
          }
          className="mt-2 h-9 px-3 rounded-lg text-sm font-medium inline-flex items-center gap-1.5 border transition hover:opacity-80"
          style={{ color: "var(--brand)", borderColor: "var(--brand)" }}
        >
          <Plus size={15} />
          Add Parameter
        </button>

        <p className="text-xs mt-2" style={{ color: "var(--main-text-muted)" }}>
          "List" splits the value on commas. Keys are {CUSTOM_KEY_HINT}
          {reservedParameterKeys.length > 0 && (
            <> and cannot be one of: {reservedParameterKeys.join(", ")}</>
          )}.
        </p>
      </div>

      {/* SUBMIT */}
      <div className="pt-2">
        <button
          disabled={isLoading}
          className={`w-full h-11 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors ${isLoading ? "cursor-not-allowed" : ""}`}
          style={{ background: isLoading ? "var(--card-border)" : "var(--brand)", color: "#fff" }}
        >
          {isEdit ? <RefreshCw size={18} /> : <Save size={18} />}
          {isEdit
            ? isLoading
              ? "Re-signing License..."
              : "Update & Re-sign License"
            : isLoading
              ? "Generating License..."
              : "Generate Trinetra License"}
        </button>
      </div>
    </form>
  );
}

/**
 * What an edit leaves alone, shown so the operator can confirm they are editing
 * the license they meant to — and so the two things that surprise people are
 * stated up front: the machine binding does not move, and the file already on
 * that machine keeps working until it is replaced.
 */
const LockedLicenseSummary = ({ license, clients }) => {
  const clientName =
    license.clientObjectId?.clientName ||
    clients.find((c) => c._id === license.clientObjectId)?.clientName ||
    "—";

  const rows = [
    { label: "License ID", value: license.licenseId, mono: true },
    { label: "Client", value: clientName },
    { label: "Hardware Fingerprint", value: license.hardwareFingerprint, mono: true, wrap: true },
    { label: "Type", value: license.licenseType },
    {
      label: "Expires",
      value: license.expiresAt
        ? new Date(license.expiresAt).toLocaleDateString("en-GB")
        : "Never (Perpetual)",
    },
    { label: "Revision", value: `v${license.revision || 1}` },
  ];

  return (
    <div
      className="rounded-lg border p-3"
      style={{ background: "var(--input-background)", borderColor: "var(--card-border)" }}
    >
      <p
        className="flex items-center gap-1.5 text-sm font-semibold mb-2"
        style={{ color: "var(--main-text-muted)" }}
      >
        <Lock size={14} />
        Fixed for this license
      </p>

      <dl className="space-y-1">
        {rows.map(({ label, value, mono, wrap }) => (
          <div key={label} className="flex gap-3 text-xs">
            <dt className="w-40 shrink-0" style={{ color: "var(--main-text-muted)" }}>
              {label}
            </dt>
            <dd
              className={`${mono ? "font-mono" : ""} ${wrap ? "break-all" : "truncate"}`}
              style={{ color: "var(--main-text)" }}
              title={String(value)}
            >
              {value}
            </dd>
          </div>
        ))}
      </dl>

      <p className="text-xs mt-2 pt-2 border-t" style={{ color: "var(--main-text-muted)", borderColor: "var(--card-border)" }}>
        Saving re-signs this license onto the same hardware and keeps its remaining
        validity. Send the customer the new download — the file already installed
        keeps its current entitlements until it is replaced.
      </p>
    </div>
  );
};

const BulkActions = ({ onSelectAll, onClearAll, disabled }) => (
  <span className="flex items-center gap-2 text-xs mb-1">
    <button
      type="button"
      onClick={onSelectAll}
      disabled={disabled}
      className="font-medium transition hover:opacity-70 disabled:opacity-40 disabled:cursor-not-allowed"
      style={{ color: "var(--brand)" }}
    >
      Select all
    </button>
    <span style={{ color: "var(--card-border)" }}>|</span>
    <button
      type="button"
      onClick={onClearAll}
      disabled={disabled}
      className="font-medium transition hover:opacity-70 disabled:opacity-40 disabled:cursor-not-allowed"
      style={{ color: "var(--main-text-muted)" }}
    >
      Clear
    </button>
  </span>
);

/**
 * A free-text box for keys the picker above it does not offer, with the keys
 * added so far shown as removable chips. Used for both custom models and
 * custom features — they differ only in labels and which list counts as
 * "standard".
 */
const CustomKeyAdder = ({
  label,
  placeholder,
  draft,
  onDraftChange,
  onAdd,
  items,
  onRemove,
  error,
  note,
  inputStyle,
}) => (
  <div>
    <OptionalLabel hint={`(${CUSTOM_KEY_HINT})`}>{label}</OptionalLabel>

    <div className="flex gap-2">
      <input
        value={draft}
        onChange={(e) => onDraftChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            // Otherwise Enter submits the whole form with the key unadded.
            e.preventDefault();
            onAdd();
          }
        }}
        placeholder={placeholder}
        className="flex-1 h-10 px-3 font-mono text-sm rounded-lg border focus:ring-0 transition"
        style={inputStyle}
      />
      <button
        type="button"
        onClick={onAdd}
        className="h-10 px-4 rounded-lg text-sm font-medium inline-flex items-center gap-1.5 border transition hover:opacity-80"
        style={{ color: "var(--brand)", borderColor: "var(--brand)" }}
      >
        <Plus size={15} />
        Add
      </button>
    </div>

    {items.length > 0 && (
      <div className="mt-2 flex flex-wrap gap-2">
        {items.map((key) => (
          <span
            key={key}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-mono border"
            style={{
              background: "var(--input-background)",
              color: "var(--main-text)",
              borderColor: "var(--card-border)",
            }}
          >
            {key}
            <button
              type="button"
              onClick={() => onRemove(key)}
              style={{ color: "var(--danger)" }}
              className="hover:opacity-80 transition-colors"
            >
              <X size={13} />
            </button>
          </span>
        ))}
      </div>
    )}

    {error ? (
      <p className="text-xs mt-1" style={{ color: "var(--danger)" }}>{error}</p>
    ) : (
      note && (
        <p className="text-xs mt-1" style={{ color: "var(--main-text-muted)" }}>{note}</p>
      )
    )}
  </div>
);

const ChipSelect = ({
  label,
  selected,
  available,
  onAdd,
  onRemove,
  onSelectAll,
  onClearAll,
  error,
  placeholder,
  countSuffix = "",
}) => (
  <div>
    <div className="flex items-center justify-between">
      <RequiredLabel>{label}</RequiredLabel>
      {onSelectAll && (
        <BulkActions
          onSelectAll={onSelectAll}
          onClearAll={onClearAll}
          disabled={available.length === 0 && selected.length === 0}
        />
      )}
    </div>

    {/* Selected chips area */}
    <div className="min-h-[48px] w-full rounded-lg px-3 py-2.5 flex flex-wrap gap-2 border" style={{ background: "var(--input-background)", borderColor: "var(--card-border)" }}>
      {selected.length === 0 && (
        <span className="w-full min-h-[28px] flex items-center text-sm italic" style={{ color: "var(--main-text-muted)" }}>No items selected</span>
      )}
      {selected.map((item) => (
        <span
          key={item}
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border"
          style={{ background: "var(--brand-light)", color: "var(--brand)", borderColor: "var(--brand)" }}
        >
          {item}
          <button type="button" onClick={() => onRemove(item)} style={{ color: "var(--danger)" }} className="hover:opacity-80 transition-colors">
            <X size={13} />
          </button>
        </span>
      ))}
    </div>

    {/* Dropdown to add */}
    <select
      onChange={(e) => { onAdd(e.target.value); e.target.value = ""; }}
      className="mt-2 w-full h-9 px-3 rounded-lg border text-sm focus:ring-0 transition"
      style={{ background: "var(--input-background)", color: "var(--main-text)", borderColor: "var(--card-border)" }}
    >
      <option value="">{placeholder || "Select to add"}</option>
      {available.map((item) => (
        <option key={item} value={item}>{item}</option>
      ))}
    </select>

    {/* Count + Error */}
    <div className="flex justify-between mt-1">
      {error ? (
        <p className="text-xs" style={{ color: "var(--danger)" }}>{error}</p>
      ) : (
        <span className="text-xs" style={{ color: "var(--main-text-muted)" }}>
          {selected.length} selected{countSuffix}
        </span>
      )}
    </div>
  </div>
);
